import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const root = window.Shopify?.routes?.root || '/';

  const cartUrl = `${root}cart.js`;
  const addUrl = `${root}cart/add.js`;

  async function getCart() {
    const response = await fetch(cartUrl, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Unable to check your cart.');
    }

    return response.json();
  }

  function cartContainsVariant(cart, variantId) {
    return cart.items.some(
      item => String(item.variant_id) === String(variantId)
    );
  }

  function init(section) {
    if (section.dataset.lwInitialized === 'true') return;

    section.dataset.lwInitialized = 'true';

    const formatter = new Intl.NumberFormat(
      document.documentElement.lang || 'en-AU',
      {
        style: 'currency',
        currency: section.dataset.currency || 'AUD'
      }
    );

    const cards = [
      ...section.querySelectorAll('[data-lw-card]')
    ];

    const currentCard = cards.find(
      card => card.dataset.current === 'true'
    );

    const button = section.querySelector('[data-lw-add]');
    const error = section.querySelector('[data-lw-error]');
    const totalNode = section.querySelector('[data-lw-total]');

    let cart = null;
    let busy = false;

    function syncCurrentVariant() {
      if (!currentCard) return;

      const productForm = document.querySelector(
        'product-form-component:not(.quick-add-modal product-form-component)'
      );

      const input = productForm?.querySelector('input[name="id"]');

      if (!input?.value) return;

      const matching = section.querySelector(
        `[data-lw-current-variant][data-variant-id="${CSS.escape(input.value)}"]`
      );

      if (!matching) return;

      currentCard.dataset.variantId = input.value;
      currentCard.dataset.price = matching.dataset.price;

      const priceNode = currentCard.querySelector('[data-lw-price]');

      if (priceNode) {
        priceNode.textContent = formatter.format(
          Number(matching.dataset.price) / 100
        );
      }
    }

    function isSelected(card) {
      return (
        card.dataset.current === 'true' ||
        Boolean(card.querySelector('[data-lw-select]')?.checked)
      );
    }

    function update() {
      syncCurrentVariant();

      let total = 0;
      let toAdd = 0;

      cards.forEach(card => {
        const selected = isSelected(card);

        const alreadyInCart = cart
          ? cartContainsVariant(cart, card.dataset.variantId)
          : false;

        card.classList.toggle(
          'is-selected',
          selected || alreadyInCart
        );

        const checkbox = card.querySelector('[data-lw-select]');

        if (checkbox) {
          checkbox.disabled = alreadyInCart;

          const label = checkbox.closest('label');

          if (alreadyInCart) {
            checkbox.checked = true;

            if (label) {
              label.lastChild.textContent = ' Already in your cart';
            }
          } else if (label) {
            label.lastChild.textContent = ' Add this item';
          }
        }

        if (card.dataset.current === 'true') {
          const included = card.querySelector('[data-lw-included]');

          if (included) {
            included.textContent = alreadyInCart
              ? '✓ Already in your cart'
              : '✓ Included';
          }
        }

        if (selected && !alreadyInCart) {
          total += Number(card.dataset.price || 0);
          toAdd++;
        }
      });

      if (totalNode) {
        totalNode.textContent = formatter.format(total / 100);
      }

      if (button && !busy) {
        button.textContent = toAdd
          ? 'Add selected items to cart'
          : 'View your cart';
      }
    }

    async function refreshCart() {
      cart = await getCart();
      update();
    }

    cards.forEach(card => {
      card.querySelector('[data-lw-select]')?.addEventListener(
        'change',
        update
      );

      card.querySelector('[data-lw-variant]')?.addEventListener(
        'change',
        event => {
          const option = event.target.selectedOptions[0];

          if (!option || option.disabled) return;

          card.dataset.variantId = option.value;
          card.dataset.price = option.dataset.price;

          const priceNode = card.querySelector('[data-lw-price]');

          if (priceNode) {
            priceNode.textContent = formatter.format(
              Number(option.dataset.price) / 100
            );
          }

          update();
        }
      );
    });

    document.addEventListener('change', event => {
      if (
        event.target?.matches(
          'product-form-component input[name="id"]'
        )
      ) {
        update();
      }
    });

    document.addEventListener('product:variant-change', update);

    function openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer-component');

      if (cartDrawer?.open) {
        cartDrawer.open();
        return true;
      }

      const drawer = document.querySelector('cart-drawer');

      if (drawer?.open) {
        drawer.open();
        return true;
      }

      const cartTrigger = document.querySelector(
        '[aria-controls="cart-drawer"]'
      );

      if (cartTrigger) {
        cartTrigger.click();
        return true;
      }

      return false;
    }

    button?.addEventListener('click', async () => {
      if (busy) return;

      busy = true;
      button.disabled = true;
      button.textContent = 'Checking cart…';

      if (error) {
        error.hidden = true;
      }

      try {
        await refreshCart();

        const selected = cards.filter(isSelected);

        const items = selected
          .filter(
            card =>
              !cartContainsVariant(
                cart,
                card.dataset.variantId
              )
          )
          .map(card => ({
            id: Number(card.dataset.variantId),
            quantity: 1
          }));

        if (!items.length) {
          openCartDrawer();
          return;
        }

        if (
          items.some(
            item =>
              !Number.isSafeInteger(item.id) ||
              item.id <= 0
          )
        ) {
          throw new Error(
            'Please select an available product option.'
          );
        }

        button.textContent = 'Adding to cart…';

        const sectionIds = [
          ...new Set(
            [
              ...document.querySelectorAll(
                'cart-items-component[data-section-id]'
              )
            ].map(element => element.dataset.sectionId)
          )
        ];

        const deferred = CartLinesUpdateEvent.createPromise();

        section.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map(item => ({
              merchandiseId: String(item.id),
              quantity: 1
            })),
            promise: deferred.promise
          })
        );

        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
            result.message ||
            'Unable to add the selected items.'
          );
        }

        cart = await getCart();

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            sections: result.sections,
            source: 'lw-build-their-world',
            didError: false
          }
        });

        update();

        button.textContent = 'Added to cart ✓';

        setTimeout(() => {
          openCartDrawer();
        }, 150);

      } catch (err) {
        if (error) {
          error.textContent =
            err.message ||
            'Something went wrong. Please check your cart.';

          error.hidden = false;
        }

        try {
          await refreshCart();
        } catch (_) {
          // Keep the error visible.
        }

      } finally {
        busy = false;
        button.disabled = false;

        setTimeout(update, 1800);
      }
    });

    refreshCart().catch(() => {
      // The button will check the cart again before adding.
    });

    update();
  }

  function start() {
    document
      .querySelectorAll('[data-lw-world]')
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();